import { supabase, run } from './db';
import { addDays } from '../constants/schedule';
import { RECIPIENT_STATUS } from '../constants/workflowGranular';

/**
 * A sobra da haste: o que foi descongelado e ainda não foi usado.
 *
 * A haste sai inteira do botijão, mas nem todos os embriões dela vão para a
 * mesma TE. Depois de descongelar não há volta: ou é usado em até 48 horas, ou
 * é perda (laboratório, 04/11/2026).
 *
 * O PRAZO É CONTRA A DATA DA TE, NÃO CONTRA HOJE. A TE costuma ser lançada dias
 * depois de feita; comparar com hoje faria o lançamento atrasado perder um
 * embrião que estava dentro do prazo no dia em que foi usado.
 *
 * A TE só tem data, sem hora, então as 48 horas valem como o dia do
 * descongelamento mais os dois seguintes.
 */
const DIAS_DE_PRAZO = 2;

const soData = (d) => (d ? String(d).slice(0, 10) : null);

/** Hoje no fuso do aparelho: `toISOString` já viraria amanhã à noite em BRT. */
export const hojeLocal = (agora = new Date()) => {
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
};

export const limiteDoDescongelado = (descongeladoEm) => addDays(soData(descongeladoEm), DIAS_DE_PRAZO);

/** Uma TE desta data pode usar o embrião? Antes de descongelar também não pode. */
export const dentroDoPrazo = (descongeladoEm, data) => {
  const de = soData(descongeladoEm);
  const quando = soData(data);
  if (!de || !quando) return false;
  return quando >= de && quando <= limiteDoDescongelado(de);
};

const dataCurta = (d) => {
  const [, mes, dia] = soData(d).split('-');
  return `${dia}/${mes}`;
};

/** "descongelado 04/11 · usar até 06/11" */
export const textoDoDescongelado = (descongeladoEm) =>
  `descongelado ${dataCurta(descongeladoEm)} · usar até ${dataCurta(limiteDoDescongelado(descongeladoEm))}`;

/**
 * Transferências que a TE não pode gravar: sobra aprovada fora do prazo.
 *
 * A barreira fica na tela, antes da fila offline. No banco, ela faria a TE
 * inteira, feita no curral, falhar na sincronização por causa de uma linha.
 *
 * Quem já é `transferred` fica de fora: é resultado gravado, não uso novo.
 */
export const usosForaDoPrazo = (embrioes = [], transferencias = {}, dataDaTe = hojeLocal()) =>
  embrioes.filter(e =>
    e.destination === 'frozen'
    && e.descongelado_em
    && transferencias[e.id]?.status === RECIPIENT_STATUS.APPROVED
    && !dentroDoPrazo(e.descongelado_em, dataDaTe));

/** O vencido primeiro: é ele que pede a baixa. Dentro de cada grupo, o mais antigo. */
export const ordenaPorPrazo = (linhas = [], hoje = hojeLocal()) =>
  [...linhas].sort((a, b) => {
    const va = limiteDoDescongelado(a.descongelado_em) < hoje ? 0 : 1;
    const vb = limiteDoDescongelado(b.descongelado_em) < hoje ? 0 : 1;
    return va - vb || soData(a.descongelado_em).localeCompare(soData(b.descongelado_em));
  });

/**
 * De onde veio o embrião que entrou sem serviço. Quem é de um serviço daqui
 * não passa por esta pergunta: foi feito aqui.
 */
export const ORIGENS = [
  { id: 'laboratorio', label: 'Feito aqui' },
  { id: 'outro_laboratorio', label: 'Outro laboratório' },
];

export const rotuloDaOrigem = (origem) =>
  ORIGENS.find(o => o.id === origem)?.label || 'origem não informada';

/**
 * Prévia da linha de desvitrificação, a partir dos embriões na tela da TE.
 *
 * Só avisa quem lança: a quantidade cobrada é calculada no banco ao salvar,
 * porque ali entram a sobra usada em outra TE e a que ninguém usou.
 */
export const previaDaDesvitrificacao = (embrioes = [], preco = 0) => {
  const semServico = embrioes.filter(e => !e.service_id);
  const cobrados = semServico.filter(e => e.origem === 'outro_laboratorio').length;
  const semOrigem = semServico.filter(e => !e.origem).length;
  const unitario = Number(preco) || 0;
  return { cobrados, semOrigem, preco: unitario, valor: cobrados * unitario };
};

export const listarDescongelados = (laboratoryId) =>
  run(supabase
    .from('vw_descongelados')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('descongelado_em'));
